import { buildJobPerform, buildOnTheWay, BaseJob, JobType, JobTypeLabels } from "src/app/types/job.type";
import { BuildServices, GenericRouteEvent, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from "./generic-route-event.model";
import { environment } from "src/environments/environment";
import * as helpers from '../../helpers/ui-helpers';
import { JobDetailsDialog } from "src/app/dialogs/job-details/job-details.dialog";
import { Capacitor } from "@capacitor/core";
import { DateTime } from "luxon";
import { JobPerformDialog } from "src/app/dialogs/job-perform/job-perform.dialog";
import { PassResults } from "../../services/pass.service";
import { Dropoff } from ".";
import { AppData } from "src/app/data";

export class Job extends GenericRouteEvent {

  public value: BaseJob;
  public onTheWayCompleted = !environment.features.hasOnTheWay;
  public label: string;
  public _for: string = this.translateSvc.translate('LABEL.for');
  public riderName: string;
  public type = 'job';
  public dropoff: Dropoff | null = null;

  constructor(value: BaseJob, services: BuildServices) {
    super(services);
    this.completed = value.completed || (environment.features.hasDepart ? !!value.departedTime : !!value.performTime);
    this.scheduledStart = value.scheduledTime ? DateTime.fromISO(value.scheduledTime.toString()) : null;
    this.headerInfo = {
      time: helpers.getTimeForCardHeader({ start: this.scheduledStart, end: this.scheduledEnd, translateSvc: services.translateSvc }),
      type: '',
      details: helpers.handleName(value.riderFirstName, value.riderLastName),
      timeTo: this.scheduledStart?.toRelative() || '',
    };
    this.location = value.location;
    this.stopSequence = value.sequence || 0;
    this.value = value;
    this.label = `${this.value.jobType === JobType.Pickup ? this.translateSvc.translate('LABEL.pickup') : this.translateSvc.translate('ACTION.dropOff')}`;
    this.riderName = `${ value.riderLastName }, ${ value.riderFirstName }`;
    this.actions = this.getActions();
    this.typeIcons = helpers.getMobilityIcons(value);
    this.detailIcons = helpers.getDetailsIcons(value);
    this.detailBadges = helpers.getDetailBadges(value, this.translateSvc);
    this.id = value.rideId;
    value.defaultFareTypeId = Number(value.defaultFareTypeId);
    value.additionalDefaultFareTypeId = Number(value.additionalDefaultFareTypeId);

    this.updateTimeBasedProperties();
  }

  tapForDetails = async () => {
    const detailModal = await this.modalController.create({
      cssClass: 'parascope-full-modal',
      component: JobDetailsDialog,
      componentProps: { routeEvent: this },
    });
    await detailModal.present();
    const dialog = detailModal.shadowRoot.querySelector('[role="dialog"]');
    dialog.setAttribute('aria-label', 'Job Details');
  };

  public updateTimeBasedProperties = () => {
    const timeToDiff = this.scheduledStart;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative({ unit: [ 'hours', 'minutes'  ], padding: 6000 });
  };

  public getActions(): RouteEventAction[] {
    const { arriveTime, flagStopId, jobType, performTime } = this.value;
    const label = this.translateSvc.translate(JobTypeLabels[jobType]);
    const actions: RouteEventAction[] =  [
      {
        type: RouteEventActionTypeMap.onTheWay,
        label: 'ACTION.onTheWay',
        show: helpers.canOnTheWayJob(this.value),
        icon: 'send',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.onTheWay(action),
      },
      {
        type: RouteEventActionTypeMap.arrive,
        label: `${ this.translateSvc.translate('ACTION.arrive') } ${ this.translateSvc.translate(this.label) }`,
        show: helpers.canArrive(this.value),
        icon: 'locate', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.arrive({ action }),
      },
      {
        type: RouteEventActionTypeMap.perform,
        label: 'ACTION.onboard',
        show: helpers.canOnBoardJob(this.value),
        icon: 'checkmark-circle', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.perform({ noShow: false, action, data: this.appData }),
      },
      {
        type: RouteEventActionTypeMap.perform,
        label: `ACTION.noShow`,
        show: helpers.canNoShowJob(this.value),
        icon: 'close-circle', color: RouteEventActionColorMap.danger, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.perform({ noShow: true, action, data: this.appData }),
      },
      {
        type: RouteEventActionTypeMap.perform,
        label: `ACTION.dropOff`,
        show: jobType === JobType.DropOff && !!arriveTime && !flagStopId && !performTime,
        icon: 'trending-down', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: async (action: RouteEventAction) => this.perform({ noShow: false, action, data: this.appData }),
      },
      {
        type: RouteEventActionTypeMap.depart,
        label: `${ this.translateSvc.translate('ACTION.depart') } ${ this.translateSvc.translate(this.label) }`,
        show: this.onTheWayCompleted && this.value.arriveTime && environment.features.hasDepart && this.value.performTime,
        icon: 'log-out', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.depart({ action }),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        show: this.value.jobEnabled && helpers.hasValidGeo(this.location),
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
      {
        type: RouteEventActionTypeMap.notify,
        label: `ACTION.notify`,
        show: this.enabled && jobType === JobType.Pickup && environment.features.notifyPassenger,
        icon: 'call', color: RouteEventActionColorMap.medium, fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: async (action: RouteEventAction) => this.notify(action),
      },
    ].filter(action => action.show);
    return actions;
  }

  onTheWay = async (action?: RouteEventAction) => {
    try {
      if (action) { action.disabled = true; }
      const gpsReport = this.appData.gpsReport;
      const { latitude, longitude, dateTimeReported } = gpsReport;
      const estimatedTimeOfArrival: Date = await this.locationSvc.estimatedTimeOfArrival({ latitude, longitude, date: dateTimeReported });

      const onTheWay = buildOnTheWay({ job: this.value, estimatedTimeOfArrival, gpsReport });
      const onTheWays = [ onTheWay ];

      //  todo: handle passing in route events
      const similarJobs = helpers.getSimilarJobs({ can: helpers.canOnTheWayJob, anchor: this, routeEvents: [] });
      const additionalOnTheWays = similarJobs.map(otherJob => buildOnTheWay({ job: otherJob.value, estimatedTimeOfArrival, gpsReport }));
      const dialog = {
        header: this.translateSvc.translate('LABEL.reportJobsOTW'),
        message: similarJobs.reduce((acc, { _for, riderName }) => {
          acc += `<br>${_for} ${ riderName }`;
          return acc;
        }, ''),
      };
      const includeSimilarJobs = await helpers.includeSimilarJobs({ additionalJobCount: similarJobs.length, dialog, alertController: this.alertController });
      if (includeSimilarJobs) { onTheWays.push.apply(onTheWays, additionalOnTheWays); }

      await this.routeProviderSvc.reportJobsOnTheWay({ onTheWays });
      const now = new Date();
      [ this, ...(includeSimilarJobs ? similarJobs : []) ].forEach(_job => {
        _job.value.onTheWayTime = now.toISOString();
        _job.actions = _job.getActions();
        _job.onTheWayCompleted = true;
      });
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { action.disabled = false; }
    }
  };

  arrive = async ({ action, checkLocation = true, checkOdometer = false }) => {
    try {
     if (action) action.disabled = true;
      const hasArrived = checkLocation ? await this.locationSvc.checkHasArrived([ this as GenericRouteEvent ]) : true;
      if (!hasArrived) { return; }
      const jobsToArrive = [ this ];
      //  todo: handle passing in route events
      const similarJobs = helpers.getSimilarJobs({ can: helpers.canArrive, anchor: this, routeEvents: [] });
      const dialog = {
        header: this.translateSvc.translate('LABEL.arriveJobs'),
        message: similarJobs.reduce((acc, { label, _for, riderName }) => {
          acc += `<br>${ label } ${ _for } ${ riderName }`;
          return acc;
        }, ''),
      };
      const includeSimilarJobs = await helpers.includeSimilarJobs({ additionalJobCount: similarJobs.length, dialog, alertController: this.alertController });
      if (includeSimilarJobs) { jobsToArrive.push.apply(jobsToArrive, similarJobs); }
      await this.dispatchSvc.arriveJobs(jobsToArrive as GenericRouteEvent[]);
      const now = new Date();
      [ this, ...(includeSimilarJobs ? similarJobs : []) ].forEach(_job => {
        _job.value.arriveTime = now.toISOString();
        _job.actions = _job.getActions();
      });
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

  public async perform({ noShow, action , data}: { noShow: boolean, action: RouteEventAction, data: AppData }): Promise<void> {
    try {
      action.disabled = true;
      const jobPerform = buildJobPerform({ job: this.value, data: this.appData, signature: null, signatureReason: null, noShowReason: null });
      if (this.value.riderSignatureRequired && !noShow) {
        const signatureRes = await helpers.getSignature({ job: this.value, modalController: this.modalController });
        if (signatureRes.role === 'cancel') { return; }
        const { signature, signatureReason } = signatureRes.data;
        jobPerform.signature = signature;
        jobPerform.signatureReason = signatureReason;
      }
      const performDialog = await this.modalController.create({
        cssClass: 'parascope-full-modal',
        component: JobPerformDialog,
        id: 'jobPerformDialog',
        componentProps: { props: { routeEvent: this, jobPerform, noShow }},
      });
      await performDialog.present();
      const dialog = performDialog.shadowRoot.querySelector('[role="dialog"]');
      dialog.setAttribute('aria-label', 'Perform Job');
      const res = await performDialog.onDidDismiss();
      if (res.role !== 'submit') { return; }

      this.value.performTime = new Date().toISOString();

      if (this.dropoff?.value) {
        this.dropoff.value.jobEnabled = true;
        if (this.value.riderSignatureRequired && res.data.signature) {
           this.dropoff.value.riderSignatureRequired = !res.data.signature;
        }
        if (res.data?.noShowReason !== null) {
          this.dropoff.value.completed = true;
        }
      }

      if (!environment.features.hasDepart) { this.value.completed = true; }
      const update = [ this.value ];
      if (this.dropoff?.value) { update.push(this.dropoff.value ); }
      this.appData.setJobs(update);
      this.actions = this.getActions();
      if (!environment.features.hasDepart) { this.completed = true; }

    } catch(error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      action.disabled = false;
    }
  }

  public depart = async ({ action }) => {
    try {
      if (action) action.disabled = true;
      await this.dispatchSvc.departJobs([ this ]);
      const now = new Date();
      [ this ].forEach(_job => {
        _job.value.departedTime = now.toISOString();
        this.completed = true;
        _job.actions = _job.getActions();
      });
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

  public captureSignature(): Promise<helpers.SignatureResponse> {
    return helpers.getSignature({ job: this.value, modalController: this.modalController });
  }

  public async scanPass(ePassId: string = null): Promise<PassResults | null> {
    const passInfo = await this.passSvc.handleScanPass({ job: this, ePassId });
    return passInfo;
  }

  public notify = async (action: RouteEventAction, displayNotifications: boolean = true): Promise<void> => {
    try {
      if (action) action.disabled = true;
      let confirm = false;
      if (displayNotifications) {
        const alert = await this.alertController.create({
           header: this.translateSvc.translate('TITLE.passengerNotify'),
           message: `${this.translateSvc.translate('LABEL.reqDispatchCall')} ${this.riderName}?`,
           buttons: [{
             text: this.translateSvc.translate('LABEL.yes'),
             cssClass: 'primary',
             handler: () => { confirm = true; },
            }, {
              text: this.translateSvc.translate('LABEL.no'),
              cssClass: 'secondary',
              handler: () => { confirm = false; },
            }],
          });
        await alert.present();
        await alert.onDidDismiss();
        if (!confirm) { return; }
      }
      const request = { rideId: this.value.rideId, gpsReport: this.appData.gpsReport };
      await this.dispatchSvc.callRequest(request);
      if (displayNotifications) { await this.notificationSvc.success(this.translateSvc.translate('LABEL.reqSent')); }
    } catch (error) {
      if (displayNotifications) {
        this.notificationSvc.error(error?.message || error);
      } else {
        throw error;
      }
    } finally {
      if (action) action.disabled = false;
    }
  };

}
